import React, { Fragment, PureComponent } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import {
  LoadingSpinner,
  observable,
  ToastNotifications,
} from 'legacy-packages/ft-cb';
import { v4 as uuidv4 } from 'uuid';
import isEmpty from 'lodash/isEmpty';
import defer from 'lodash/defer';
import cn from 'classnames';

import Icon from 'chameleon/ui-stack/ui/base/icon';
import log from 'chameleon/ui-stack/utilities/log';
import { destroyPlayer } from 'chameleon/ui-stack/hooks/use-learnosity-provider';
import ToastContentWrapper from 'chameleon-src/components/wrappers/ToastContentWrapper';
import PlayerModal from 'legacy-learnosity-player/src/utility/PlayerModal';
import ResponsivePlayerWrapper from './components/ResponsivePlayerWrapper';
import HelpMenu from './components/HelpMenu';
import ExpiredTimeConfirmationModal from './components/Timer/ExpiredTimeConfirmationModal';
import SubmitConfirmationPopover from './components/SubmitConfirmationPopover';
import PracticeConfirmationPopover from './components/PracticeConfirmationPopover';
import ExitAssignmentModal from './components/ExitAssignmentModal';
import { DummyExitModal, DummySubmitModal } from './components/DummyModals';

import '../utility/player.scss';
import {
  removeDomElement,
  removeDomElements,
} from '../utility/redesignHelpers';

const DESKTOP_BREAKPOINT = 768;

class Player extends PureComponent {
  static displayName = 'LearnosityPlayer';

  static propTypes = {
    onMountCallback: PropTypes.func,
    readyListenerCallback: PropTypes.func,
    onQuestionChanged: PropTypes.func,
    onSubmitAssignmentSuccess: PropTypes.func,
    onSubmitAssignmentError: PropTypes.func,
    overviewBackLink: PropTypes.string,
    learnosityServerData: PropTypes.object,
    questionIds: PropTypes.arrayOf(PropTypes.string),
    isSubmittable: PropTypes.bool,
    isDummyPlayer: PropTypes.bool,
    fullScreen: PropTypes.bool,
    preview: PropTypes.bool,
    allowCalculatorForItems: PropTypes.array,
    fullName: PropTypes.string,
    timerInfo: PropTypes.object,
    timer: PropTypes.string,
    onExitPlayer: PropTypes.func,
    annotationData: PropTypes.object,
    appolloClient: PropTypes.func,
    responsiveView: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    mobileMedia: PropTypes.object,
    navigation: PropTypes.element,
    showQuestionInfo: PropTypes.bool,
    showQuestionOverview: PropTypes.bool,
    showCalculatorAllowedIcon: PropTypes.bool,
    showQuestionIndex: PropTypes.bool,
    showQuestionBookmark: PropTypes.bool,
    checkTimeExtension: PropTypes.func,
    checkTimeExtensionInterval: PropTypes.number,
    onTimerExpiring: PropTypes.func,
    isFinalExam: PropTypes.bool,
    // Flag for auto submit to Learnosity once the assignment gets locked.
    shouldAutoSubmit: PropTypes.bool,
    enableAudioPlaybackComplete: PropTypes.bool,
    formulaSheet: PropTypes.object,
    periodicTable: PropTypes.object,
  };

  static defaultProps = {
    onMountCallback: () => {},
    readyListenerCallback: () => {},
    onQuestionChanged: () => {},
    onSubmitAssignmentSuccess: () => {},
    onSubmitAssignmentError: () => {},
    checkTimeExtension: () => {},
    onExitPlayer: () => {},
    appolloClient: () => {},
    // trigger start/stop practice, display SQ for students on ui-fym
    onPracticeFlowToggle: () => {},
    fullName: '',
    timerInfo: {},
    timer: '',
    annotationData: null,
    questionIds: [],
    containerElement: 'learnosity_assess',
    apiType: 'items',
    overviewBackLink: null,
    learnosityServerData: null,
    isSubmittable: false,
    isDummyPlayer: false,
    fullScreen: false,
    preview: false,
    allowCalculatorForItems: [],
    responsiveView: null,
    mobileMedia: null,
    navigation: null,
    showQuestionInfo: true,
    showQuestionOverview: true,
    showCalculatorAllowedIcon: true,
    showQuestionIndex: true,
    showQuestionBookmark: true,
    checkTimeExtensionInterval: 30000,
    onTimerExpiring: null,
    isFinalExam: false,
    shouldAutoSubmit: false,
    enableAudioPlaybackComplete: false,
    // support of or not cb practice assignments, show SQ to students
    followPracticeFlow: false,
    formulaSheet: null,
    periodicTable: null,
  };

  static getDerivedStateFromProps(nextProps) {
    observable.emit('timerInfo', nextProps.timerInfo);

    return null;
  }

  constructor(props) {
    super(props);

    const { onBackClick } = props;

    this.playerId = uuidv4();
    this.playerContainerId = `player-${this.playerId}`;

    this.state = {
      isLearnosityReady: false,
      questionChanged: false,
    };

    this.itemsPlayer = {};
    this.app = {};
    this.mathJaxProcessed = [];
    this.onExitPlayerHandlers = [];

    // question navigation
    this.goToNextQuestion = this.goToNextQuestion.bind(this);
    this.goToPreviousQuestion = this.goToPreviousQuestion.bind(this);
    this.goToQuestion = this.goToQuestion.bind(this);

    // assignment
    this.itemLoad = this.itemLoad.bind(this);
    this.redesignPlayer = this.redesignPlayer.bind(this);
    this.onBackClick = onBackClick;

    this.setOverviewActiveCurrentItem =
      this.setOverviewActiveCurrentItem.bind(this);
    this.onOverviewPopoverOpen = this.onOverviewPopoverOpen.bind(this);
    this.onGoToQuestion = this.onGoToQuestion.bind(this);
    this.replaceNavArrows = this.replaceNavArrows.bind(this);
    this.getCurrentItemIndex = this.getCurrentItemIndex.bind(this);
    this.initWordCounterA11y = this.initWordCounterA11y.bind(this);

    this.readyListener = this.readyListener.bind(this);
  }

  componentDidMount() {
    const { onMountCallback, mobileMedia } = this.props;
    if (mobileMedia) {
      mobileMedia.addListener(this.onMediaQueriesChange.bind(this));
    }

    if (window.innerWidth < DESKTOP_BREAKPOINT) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.target.classList?.contains('nav-count')) {
            mutation.addedNodes.forEach((node) => {
              if (node.classList?.contains('current-item-pos')) {
                const newValue = node.innerText.replace(' of ', '/');
                node.innerText = newValue;
              }
            });
          }
        });
      });

      this.setState({
        observer,
      });
    }

    onMountCallback(this.props);
  }

  componentDidUpdate(prevProps) {
    const { shouldAutoSubmit } = this.props;
    const { shouldAutoSubmit: prevShouldAutoSubmit } = prevProps;

    if (shouldAutoSubmit && !prevShouldAutoSubmit) {
      this.learnositySubmit();
    }
  }

  componentWillUnmount() {
    const { mobileMedia } = this.props;
    if (mobileMedia) {
      mobileMedia.removeListener(this.onMediaQueriesChange);
    }
    observable.destroy();

    this.state?.observer?.disconnect();
    this.onExitPlayerHandlers = [];
    this.clearModalContainer();
  }

  /**
   * Media query change event handler
   * @param {object} event
   * */
  onMediaQueriesChange(event) {
    const player = document.querySelector(`#player-${this.playerId}`);
    if (player) {
      if (event.matches) {
        player.classList.add('is-mobile');
      } else {
        player.classList.remove('is-mobile');
      }
    }
  }

  // eslint-disable-next-line react/sort-comp
  questionChanged(itemIndex) {
    const { questionIds, onQuestionChanged } = this.props;
    onQuestionChanged(questionIds && questionIds[itemIndex]);
    this.setState({ questionChanged: true }, () =>
      this.forceUpdate(() => this.setState({ questionChanged: false })),
    );
  }

  goToQuestion(reference) {
    this.itemsPlayer.goto(reference);
  }

  goToNextQuestion() {
    this.itemsPlayer.next();
  }

  goToPreviousQuestion() {
    this.itemsPlayer.previous();
  }

  /**
   * Event handler for when item has loaded
   * @note initial render of the item is not triggering the event
   * */
  itemLoad() {
    this.prevItem = this.currentItem;
    this.currentItem = this.app.getCurrentItem();
  }

  /**
   * Get current item index
   * */
  getCurrentItemIndex() {
    return this.itemReferences.indexOf(this.currentItem.reference);
  }

  /**
   * Set active current item
   * @param {object} prevItemReference
   * @param {object} currentItemReference
   * */
  // eslint-disable-next-line class-methods-use-this
  setOverviewActiveCurrentItem(prevItemReference, currentItemReference) {
    if (prevItemReference) {
      document
        .querySelector(`#overview-item-${prevItemReference}`)
        .classList.remove('active');
    }

    document
      .querySelector(`#overview-item-${currentItemReference}`)
      .classList.add('active');
  }

  onOverviewPopoverOpen() {
    this.setOverviewActiveCurrentItem(
      this.prevItem.reference,
      this.currentItem.reference,
    );
  }

  /**
   * Navigate to selected question
   * @param {string} reference The question reference
   * @param {object} event The click event
   * */
  onGoToQuestion(reference, event) {
    const itemsObj = this.app.items();
    itemsObj.goto(reference);

    const clickedElementReference =
      event.target.dataset.reference ||
      event.target.parentNode.dataset.reference;
    this.setOverviewActiveCurrentItem(
      this.currentItem.reference,
      clickedElementReference,
    );
  }

  /**
   * Redesign player
   * */
  redesignPlayer() {
    /**
     * Remove the test submit elements first
     * - are actually hidden submit buttons until next button hides at last question
     * */
    const { isPreApSubject } = this.props;
    const container = document.querySelector(`#${this.playerContainerId}`);
    removeDomElements('.lrn_btn.test-submit', container);
    removeDomElements('.lrn_btn.test-review-screen', container);
    this.nextNavButtons = container.querySelectorAll('.item-next');
    this.prevNavButtons = container.querySelectorAll('.item-prev');
    const { showGrayTopRightBarPreview } = this.props;

    container.querySelector('.lrn-group-top').className += ` Navigation${
      showGrayTopRightBarPreview ? ' Navigation--medium' : ''
    }`;
    if (isPreApSubject) {
      container.querySelector('.lrn-group-top').className +=
        ` --navi-margin --side-padded`;
    }

    this.setScrollBarFor2ColumLayout();

    /**
     * Force navigation arrow container width to match question content
     * */

    removeDomElement('.menu-overlay');
    this.replaceNavArrows();
    this.initWordCounterA11y();
  }

  // eslint-disable-next-line class-methods-use-this
  initWordCounterA11y() {
    const textAreaContainers = document.querySelectorAll('.lrn_texteditor');
    textAreaContainers.forEach((textAreaContainer, index) => {
      const textArea = textAreaContainer.querySelector(
        '.lrn_texteditor_editable',
      );
      const wordCountElement = textAreaContainer.querySelector('.sr-only');
      if (wordCountElement) {
        const wordCounterId = `wordcounter_${index}`;
        wordCountElement.id = wordCounterId;
        textArea.setAttribute('aria-describedby', wordCounterId);
      }
    });
  }

  /**
   * Replace nav arrows
   * */
  replaceNavArrows() {
    // don't do anything with arrows if there is custom navigation
    if (this.props.navigation) {
      return;
    }
    const { showUpdatedCharmBracelet } = this.props;
    const isMobile = this.isMobile();
    const navArrow = <Icon id="nav-arrow" width="10" height="16" />;
    const playerContainer = document.querySelector(
      `#${this.playerContainerId}`,
    );
    removeDomElements('.item-prev', playerContainer);
    removeDomElements('.item-next', playerContainer);
    const navContainer = document.createElement('div');
    const navClasses = ['nav-container'];
    if (
      this.props.assignment?.hasImmediateFeedback &&
      this.props.immediateFeedbackFF
    ) {
      navClasses.push('check-answer-container');
    }
    navContainer.classList.add(...navClasses);
    const itemCounter = playerContainer.querySelector(
      '.top-right-wrapper .item-count',
    );

    if (isMobile) {
      const container = playerContainer.querySelector(
        '.Navigation .top-right-wrapper',
      );
      container.appendChild(navContainer);
    } else {
      if (showUpdatedCharmBracelet) {
        removeDomElements('.top-right-wrapper .item-count', playerContainer);
      }
      const container = playerContainer.querySelector(
        '.lrn-region .slides-container',
      );
      container.appendChild(navContainer);
    }

    const prevNavButton = this.prevNavButtons[0];
    navContainer.appendChild(prevNavButton);
    ReactDOM.render(navArrow, prevNavButton);
    prevNavButton.classList.remove(...prevNavButton.classList);
    prevNavButton.classList.add('nav-button');
    prevNavButton.classList.add('nav-prev');

    if (isMobile) {
      itemCounter.classList.remove(...itemCounter.classList);
      itemCounter.classList.add('nav-count');
      navContainer.appendChild(itemCounter);

      this.state.observer.observe(
        document.querySelector('.top-right-wrapper'),
        {
          childList: true,
          subtree: true,
        },
      );

      this.setState({
        // eslint-disable-next-line object-shorthand
        itemCounter: itemCounter,
      });
    }

    const nextNavButton = this.nextNavButtons[0];
    navContainer.appendChild(nextNavButton);
    ReactDOM.render(navArrow, nextNavButton);
    nextNavButton.classList.remove(...nextNavButton.classList);
    nextNavButton.classList.add('nav-button');
    nextNavButton.classList.add('nav-next');
  }

  setScrollBarFor2ColumLayout() {
    const { isPreApSubject } = this.props;
    const container = document.querySelector(`#${this.playerContainerId}`);

    if (isPreApSubject) {
      const slidesContainer = container.querySelector('.slides-container');
      if (slidesContainer) {
        const scrollableContainers = slidesContainer.querySelectorAll(
          '.slides-control > .learnosity-item.lrn-scrollable-container.item.lrn-assess-item',
        );

        scrollableContainers.forEach((scrollableContainer) => {
          const scrollablePassages = scrollableContainer.querySelectorAll(
            `.lrn.lrn_widget.lrn_feature.lrn_scrollablepassage`,
          );
          if (scrollablePassages && scrollablePassages.length) {
            scrollablePassages.forEach((passage) =>
              passage.setAttribute('tabindex', 0),
            );
            if (scrollablePassages.length === 2) {
              scrollablePassages[0].parentElement.parentElement.classList.add(
                '--overflow-fix',
              );
              // If we want to fix the scrolling for the second column, just add the class above to both of them
            }
          }
        });
      }
    }
  }

  // Only case that is going to be hard-submit for now
  // is the Final Exam Pilot. APC in both AP and PREAP
  // will have soft time
  isSoftTimer = () => !this.props.isFinalExam;

  renderModalContent = (modal, extraProps = {}) => {
    const {
      onExitPlayer,
      annotationData,
      isPreApSubject,
      timer,
      followPracticeFlow,
      onPracticeFlowToggle,
      isImpersonating,
      areMcqQuestions,
      dueAt,
      timezone,
      allowLateSubmissionFF,
    } = this.props;

    const { initiatorElement } = extraProps;
    const _handleCloseModal = () => {
      // if we have the initiatorElement that triggered this modal to open
      // then lets refocus on it when the modal closes
      this.handleCloseModal();
      if (initiatorElement) {
        initiatorElement.focus();
      }
    };
    const commonProps = {
      onClose: _handleCloseModal,
      onShowModal: this.handleOpenModal,
      isSoftTime: this.isSoftTimer(),
      isPreApSubject,
    };
    const submitProps = {
      ...commonProps,
      onExitPlayer,
      followPracticeFlow,
      onPracticeFlowToggle,
      learnositySubmit: this.learnositySubmit,
      learnositySave: this.learnositySave,
      goToQuestion: this.goToQuestion,
      app: this.app,
      isImpersonating,
      areMcqQuestions,
    };

    const exitProps = {
      ...commonProps,
      timer,
      onSubmit: (isImpersonating && onExitPlayer) || this.learnositySave,
    };

    const dummySubmitProps = {
      ...commonProps,
      onSubmit: () => this.handleOpenModal('DummyExit'),
    };

    const dummyExitProps = {
      ...commonProps,
      submitBtnText: 'Exit',
      onSubmit: onExitPlayer,
    };

    const exitConfirmationProps = {
      ...exitProps,
      dueAt,
      timezone,
      allowLateSubmissionFF,
    };

    const modals = {
      HelpMenu: () => <HelpMenu {...commonProps} {...extraProps} />,
      ExpiredTime: () => <ExpiredTimeConfirmationModal {...submitProps} />,
      SubmitConfirmation: () => <SubmitConfirmationPopover {...submitProps} />,
      PracticeConfirmation: () => (
        <PracticeConfirmationPopover {...submitProps} />
      ),
      DummySubmit: () => <DummySubmitModal {...dummySubmitProps} />,
      DummyExit: () => <DummyExitModal {...dummyExitProps} />,
      ExitConfirmation: () => (
        <ExitAssignmentModal {...exitConfirmationProps} />
      ),
    };

    const CurrentModal = () => {
      if (typeof modal === 'string') {
        return modals[modal]();
      }
      return React.createElement(modal, {
        annotationData,
        ...commonProps,
        ...extraProps,
      });
    };
    return <CurrentModal /> || null;
  };

  learnositySave = () => {
    const { onExitPlayer, isImpersonating } = this.props;

    // in impersonation mode, don't save to LRN
    if (isImpersonating) {
      return true;
    }

    const saveSetting = {
      success: () => {
        this.beforeExitPlayer();
        onExitPlayer();
      },
      error: () => {},
    };
    this.app.save(saveSetting);
  };

  learnositySubmit = () => {
    const {
      onSubmitAssignmentSuccess,
      onSubmitAssignmentError,
      isImpersonating,
    } = this.props;

    // in impersonation mode, do not try to submit to LRN
    if (isImpersonating) {
      return true;
    }

    observable.emit('submissionReady', false);
    const submitSuccess = (responses) => {
      onSubmitAssignmentSuccess(responses);
      try {
        /**
         * Learnosity fix for allowing proctoring tool to receive finish event
         * Fix proposed by learnosity team
         * */
        const { app } = this;

        // After the assessment is submitted to learnosity
        // Destoroy the player and don't allow further changes
        defer(() => {
          observable.emit('submissionReady', true);
          destroyPlayer(app);
        });
      } catch (error) {
        log('error', error);
        setTimeout(() => {
          observable.emit('submissionReady', true);
          destroyPlayer(this.app);
        }, 5000);
      }
    };
    const submitSettings = {
      success: submitSuccess,
      error: (e) => {
        onSubmitAssignmentError(e);
      },
    };
    this.app.submit(submitSettings);
  };

  /**
   * Trigger all registered handlers
   */
  beforeExitPlayer = () => {
    this.onExitPlayerHandlers.forEach((fn) => fn());
  };

  /**
   * Open modal handler
   * */
  handleOpenModal = (modalType, extraProps) => {
    const content = this.renderModalContent(modalType, extraProps);

    if (content) {
      const playerContainer = this.getPlayerContainer();
      ReactDOM.render(
        <PlayerModal container={playerContainer}>{content}</PlayerModal>,
        playerContainer,
      );
    }
  };

  /**
   * Close modal handler
   * */
  handleCloseModal = () => {
    this.clearModalContainer();
  };

  /**
   * Clear modal container element
   * */
  clearModalContainer = () => {
    const playerContainer = this.getPlayerContainer();
    if (playerContainer) {
      ReactDOM.unmountComponentAtNode(playerContainer);
    }
  };

  /**
   * Get player container
   * Cache the selector
   * */
  getPlayerContainer = () => {
    if (!this.playerModalContainer) {
      this.playerModalContainer = document.querySelector(
        `#player-${this.playerId} .player-modal-container`,
      );
    }

    return this.playerModalContainer;
  };

  isMobile = () => window.innerWidth < DESKTOP_BREAKPOINT;

  /**
   * Callback handler called when app has initialized
   * @param {object} app The main app handler
   * */
  readyListener(app) {
    const {
      mobileMedia,
      questionId,
      questionIds,
      readyListenerCallback,
      learnosityServerData: {
        request: { items },
      },
      checkTimeExtension,
      enableAudioPlaybackComplete,
    } = this.props;

    const isFirstQuestionRequested =
      !questionId || questionId === questionIds[0];

    if (mobileMedia) {
      /**
       * Call event callback to detect mobile
       * */
      this.onMediaQueriesChange(mobileMedia);
    }

    this.app = app;
    this.itemsPlayer = app.items();

    this.setState({
      isLearnosityReady: true,
    });

    if (!isFirstQuestionRequested) {
      this.goToQuestion(questionIds.indexOf(questionId));
    }

    const assessApp = app.assessApp();
    const customAssetsUrl =
      'https://questions-va.learnosity.com/latest/customassets';
    const calculatorJsFile = `${customAssetsUrl}/redirect?customType=calculator-feature-v2&assetType=js&clientReference=988e92a0`;
    const calculatorCssFile = `${customAssetsUrl}/redirect?customType=calculator-feature-v2&assetType=css&clientReference=988e92a0`;

    const generateCalculatorFeature = (calculator_type) => ({
      type: 'customfeature',
      js: calculatorJsFile,
      css: calculatorCssFile,
      width: '640',
      height: '420',
      calculator_type,
      lrn_theme: false,
    });

    const loadCalculatorFeature = () => {
      assessApp.append({
        features: [
          generateCalculatorFeature('scientific'),
          generateCalculatorFeature('graphing'),
        ],
      });
    };

    const totalLearnosityItems = items.length;
    // learnosity only do items:lazyload:batch request when the
    // items on a quiz is more than 2
    if (totalLearnosityItems <= 2) {
      loadCalculatorFeature();
    } else {
      // for large item we want to append feature when lazyload:complete
      // this helps the player not to crash when resuming a quiz
      app.on('items:lazyload:complete', () => {
        // Note: student might not be able to use the calculator feature until all items
        // are loaded completely
        loadCalculatorFeature();
        this.setScrollBarFor2ColumLayout();
      });
    }
    app.on('item:changed', this.questionChanged.bind(this));
    app.on('item:changed', checkTimeExtension);
    if (enableAudioPlaybackComplete) {
      this.performQuestionAudioComplete(app);
    }
    readyListenerCallback(
      questionIds && questionIds[0],
      isFirstQuestionRequested,
      app,
    );
    this.redesignPlayer();
  }

  performQuestionAudioComplete = (app) => {
    const audioQuestionCache = {};
    const hideQuestionDiv = (
      simpleFeatures,
      currentItem,
      responseId,
      isSharePassage,
    ) => {
      const {
        simplefeature_ids: simpleFeatIds,
        reference,
        questions,
        features,
      } = currentItem;
      Object.keys(simpleFeatures).forEach((featId) => {
        let containsAudioSimpleFeat = true;
        if (isSharePassage) {
          /**
           * only check for audio playback:complete event in the currentItem.simpleFeatIds
           * some features for this current item has keys with simpleFeatIds_1, simpleFeatIds_2, ...
           */
          containsAudioSimpleFeat = simpleFeatIds.some((s) =>
            featId.startsWith(s),
          );
        }
        if (containsAudioSimpleFeat) {
          const feat = simpleFeatures[featId];
          // eslint-disable-next-line no-prototype-builtins
          const hasAudioPlayer = feat.hasOwnProperty('audio');
          let questionHasAudioContent = false;
          if (isSharePassage) {
            questionHasAudioContent = features.some((f) =>
              f.content.includes(`data-type="audioplayer"`),
            );
          } else {
            questionHasAudioContent = questions.some((q) =>
              q.stimulus.includes(`data-type="audioplayer"`),
            );
          }
          if (hasAudioPlayer && questionHasAudioContent) {
            let questionResponseDiv = document.getElementById(responseId);
            if (!isSharePassage) {
              questionResponseDiv =
                questionResponseDiv &&
                questionResponseDiv.getElementsByClassName(
                  'lrn_response_wrapper',
                )[0];
            }
            if (questionResponseDiv) {
              /**
               * TODO look for a better way to hide all
               * audio mcq question after items:lazyload:complete
               * currently getting the app.questions[ref].simpleFeatures()
               * returns an empty object due to features not fully loaded
               * even when the items load is complete
               */
              questionResponseDiv.classList.add('hide-audio-details');
            }
            audioQuestionCache[reference] = reference;
            feat.on('playback:complete', () => {
              const playbackCurrentItem = app.getCurrentItem();
              // some sharedpassage share the same feature id, so only
              // disaply the div for the currentItem we just listened to
              const displayDetails =
                playbackCurrentItem.reference === reference &&
                questionResponseDiv;
              if (displayDetails) {
                questionResponseDiv.classList.remove('hide-audio-details');
              }
            });
          }
        }
      });
    };

    const checkAudioFeature = () => {
      const currentItem = app.getCurrentItem();
      const { questions, reference, features: feats } = currentItem;
      const isSharePassage = feats.some((f) => f.type === 'sharedpassage');
      const features = app.features();
      // dont hide the details if the student had listened to that audio question
      if (!(reference in audioQuestionCache)) {
        const appQuestions = app.questions();
        questions.forEach((questionInfo) => {
          // hide all mcq audio  question
          const { type, response_id: responseId } = questionInfo;
          if (type === 'mcq') {
            // look into all features in app.features for shared passages question
            if (isSharePassage) {
              Object.values(features).forEach((feature) => {
                const simpleFeats = feature.simpleFeatures();
                hideQuestionDiv(simpleFeats, currentItem, responseId, true);
              });
            } else {
              const simpleFeatures = appQuestions[responseId].simpleFeatures();
              hideQuestionDiv(simpleFeatures, currentItem, responseId, false);
            }
          }
        });
      }
    };
    app.on('item:load', checkAudioFeature);
  };

  render() {
    const {
      learnosityServerData,
      checkTimeExtension,
      checkTimeExtensionInterval,
      onTimerExpiring,
      preview,
      mobileMedia,
      isDummyPlayer,
    } = this.props;

    const { questionChanged } = this.state;

    const { isLearnosityReady } = this.state;

    if (isEmpty(learnosityServerData)) {
      return <LoadingSpinner data-test-learnosity-loading />;
    }

    const { title } = learnosityServerData.request.config;

    const commonProps = {
      beforeExitPlayer: (cb) => this.onExitPlayerHandlers.push(cb),
      onShowModal: this.handleOpenModal,
      onCloseModal: this.handleCloseModal,
      isLearnosityReady,
      app: this.app,
      preview,
      isDummyPlayer,
    };
    const isSoftTime = this.isSoftTimer();

    const allProps = {
      ...this.props,
      isSoftTime,
      commonProps,
      title,
      readyListener: this.readyListener,
      checkTimeExtension,
      checkTimeExtensionInterval,
      onTimerExpiring,
      questionChanged,
      itemCounter: this.state.itemCounter,
      playerContainerId: this.playerContainerId,
    };

    return (
      <Fragment>
        <div
          id={this.playerContainerId}
          // when using hidden attribute we can not calculate dimensions of elements correctly
          className={cn('Player', {
            '--out-of-screen': !isLearnosityReady,
            'is-mobile': mobileMedia && mobileMedia.matches,
          })}
          data-test-learnosity-player
        >
          <div className="player_content">
            <ResponsivePlayerWrapper {...allProps} />
          </div>
          <div className="player-modal-container" />
        </div>
        {!isLearnosityReady && (
          <LoadingSpinner data-test-learnosity-internal-loading />
        )}
        <ToastNotifications />
        <ToastContentWrapper />
      </Fragment>
    );
  }
}

export default Player;
